// Rutas de la app y guardia del recorrido (documento 07): el chofer nunca elige a dónde ir, la app lo lleva a la
// pantalla que corresponde según el estado de su día.
import { useToast } from '@reparto/ui';
import { hoyISO } from '@reparto/shared/nucleo';
import { lazy, type ReactNode, Suspense, useEffect, useRef } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate } from 'react-router';
import {
  EsqueletoAcceso,
  EsqueletoGuia,
  EsqueletoMapa,
  PantallaCargando,
  PantallaConversacionChat,
  PantallaListaChat,
} from './componentes/Esqueletos';
import { MarcoGuia } from './componentes/MarcoGuia';
import { alPerderSesion } from './datos/api';
import { db } from './datos/db';
import { useEstado } from './datos/ganchos';
import { iniciarSincronizador, useSync } from './datos/sync';
import type { Paquete } from './datos/tipos';
import { pantallaSegun } from './datos/vista';
import { Acceso } from './pantallas/Acceso';
import { AccesoCelular } from './pantallas/AccesoCelular';
import { ArmarRuta } from './pantallas/ArmarRuta';
import { Carga } from './pantallas/Carga';
import { Cierre } from './pantallas/Cierre';
import { Clave } from './pantallas/Clave';
import { Movil } from './pantallas/Movil';
import { Olvide } from './pantallas/Olvide';
import { Parada } from './pantallas/Parada';
import { Perfil } from './pantallas/Perfil';
import { RutaDelDia } from './pantallas/Ruta';
import { gps } from './servicios/gps';
import {
  cambiarMovil,
  conectado,
  conectarTiempoReal,
  desconectarTiempoReal,
  emitir,
  escuchar,
} from './servicios/tiempo-real';

const Mapa = lazy(() => import('./pantallas/Mapa'));
// La guía de uso trae sus capturas y su propio código: se carga aparte, solo si se abre.
const Uso = lazy(() => import('./pantallas/Uso'));
// El chat (decisión D39) también va aparte: en la primera carga solo están el botón de la cabecera y su globito.
const ListaChat = lazy(() => import('./chat/Lista'));
const ConversacionChat = lazy(() => import('./chat/Conversacion'));
// Mientras se descarga, la pantalla del chat en gris con su marco.
const conChat = (e: ReactNode, espera: ReactNode) => <Suspense fallback={espera}>{e}</Suspense>;

type Destino = ReturnType<typeof pantallaSegun>;

const AVISO_RUTA: Record<string, string> = {
  pedido_agregado: 'El depósito agregó un pedido a tu ruta',
  entrega_deshecha: 'El depósito reabrió una entrega',
  optimizada: 'El depósito cambió el orden de tu ruta',
  reordenada: 'El depósito cambió el orden de tu ruta',
};

/**
 * Pantallas permitidas en cada momento del día (además de Perfil, que siempre está). Con la ruta armada o en curso
 * la carga se puede mirar en solo lectura (desde los pasos de la cabecera).
 */
const PERMITIDAS: Record<Destino, RegExp> = {
  '/movil': /^\/movil$/,
  '/carga': /^\/(carga|movil)$/,
  '/ruta/armar': /^\/(ruta\/armar|mapa|carga)$/,
  '/ruta': /^\/(ruta|parada\/[\w-]+|mapa|cierre|carga)$/,
  '/cierre': /^\/(cierre|parada\/[\w-]+|movil)$/,
};

/** Un paquete de otro día ya cerrado no sirve: se elige el móvil de hoy. */
function destinoDe(v: Paquete | null): Destino {
  if (v && v.fecha < hoyISO() && (!v.ruta || v.ruta.estado === 'cerrada')) return '/movil';
  return pantallaSegun(v);
}

/** Pantallas para entrar sin sesión (decisión D51): correo y contraseña, olvidé mi contraseña y el celular. */
const SIN_SESION = /^\/acceso(\/(olvide|celular))?$/;

function Guardia() {
  const { cargando, sesion, vista } = useEstado();
  const { pathname } = useLocation();
  // La guía de uso y el enlace para elegir la contraseña se ven siempre: sin sesión, con sesión y en cualquier momento
  // del día (el enlace que manda el administrador se puede abrir con la sesión abierta).
  if (pathname === '/uso' || pathname === '/clave') return <Outlet />;
  // Mientras se lee la base del celular: el esqueleto de la pantalla a la que se va (también el del acceso).
  if (cargando)
    return SIN_SESION.test(pathname) ? <EsqueletoAcceso ruta={pathname} /> : <PantallaCargando ruta={pathname} />;
  if (!sesion?.access_token) return SIN_SESION.test(pathname) ? <Outlet /> : <Navigate to="/acceso" replace />;
  const destino = destinoDe(sesion.movil_id ? vista : null);
  // Perfil y el chat se abren en cualquier momento del día.
  if (pathname === '/perfil' || /^\/chat(\/[\w-]+)?$/.test(pathname) || PERMITIDAS[destino].test(pathname))
    return <Outlet />;
  return <Navigate to={destino} replace />;
}

/** Servicios que viven mientras la app está abierta: sincronización, avisos del servidor y GPS en ruta. */
function Servicios() {
  const { sesion, vista } = useEstado();
  const { mostrar } = useToast();
  const navegar = useNavigate();
  const aviso = useSync((s) => s.aviso);
  const fijar = useSync((s) => s.fijar);
  const mostrarRef = useRef(mostrar);
  mostrarRef.current = mostrar;

  useEffect(() => iniciarSincronizador(), []);

  useEffect(
    () =>
      alPerderSesion(async (motivo) => {
        // Sin sesión no se pierde nada: el trabajo queda en el celular hasta volver a entrar.
        await db.sesion.update('actual', { access_token: '', refresh_token: '' });
        mostrarRef.current(
          motivo === 'revocada'
            ? 'El encargado cerró tu sesión. Ingresá de nuevo.'
            : 'Tu sesión venció. Ingresá de nuevo.',
          { tipo: 'error' },
        );
      }),
    [],
  );

  useEffect(() => {
    if (!aviso) return;
    mostrar(aviso, { tipo: 'info', duracionMs: 7000 });
    fijar({ aviso: null });
  }, [aviso, mostrar, fijar]);

  const token = sesion?.access_token;
  const movilId = sesion?.movil_id;
  // Con sesión el socket queda abierto todo el día (también para el chat); el móvil se avisa al elegirlo.
  useEffect(() => {
    if (!token) return;
    let fin: (() => void) | undefined;
    void conectarTiempoReal((a) => {
      if (a.tipo === 'ruta_actualizada') {
        // Lo que hizo el propio chofer no se avisa: ya lo ve en pantalla.
        const texto = AVISO_RUTA[a.motivo];
        if (texto && a.por !== 'chofer') mostrarRef.current(texto, { icono: 'sync' });
      } else if (a.tipo === 'rendicion_recibida')
        mostrarRef.current(a.por ? `${a.por} recibió tu rendición` : 'Rendición recibida', { tipo: 'ok' });
      else mostrarRef.current(a.titulo, { tipo: 'info' });
    }).then((f) => (fin = f));
    return () => (fin ? fin() : desconectarTiempoReal());
    // El token se renueva cada 15 minutos: el socket toma el nuevo al reconectar, no hace falta reabrirlo.
  }, [!!token]);
  useEffect(() => {
    if (token && movilId) cambiarMovil(movilId);
  }, [movilId]);

  // El chat se pone al día y escucha el socket desde que hay sesión (para el globito y las tildes), pero su código
  // llega después de la primera pantalla.
  useEffect(() => {
    if (!token) return;
    let fin: (() => void) | undefined;
    let vivo = true;
    // Si se abrió directo en el chat (por ejemplo, desde una notificación), sin esperar.
    const espera = window.location.pathname.startsWith('/chat') ? 0 : 800;
    const t = setTimeout(() => {
      import('./chat/motor')
        .then((m) => vivo && (fin = m.iniciarChat({ escuchar, emitir, conectado })))
        // Sin señal y sin la app guardada todavía: se intenta de nuevo la próxima vez que se abra.
        .catch(() => undefined);
    }, espera);
    return () => {
      vivo = false;
      clearTimeout(t);
      fin?.();
    };
  }, [!!token]);

  // Tocar una notificación con la app abierta: el service worker pide ir a esa pantalla (decisión D40).
  useEffect(() => {
    const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
    const alMensaje = (e: MessageEvent<{ tipo?: string; url?: string }>) =>
      e.data?.tipo === 'abrir' && e.data.url?.startsWith('/') && void navegar(e.data.url);
    sw?.addEventListener('message', alMensaje);
    return () => sw?.removeEventListener('message', alMensaje);
  }, [navegar]);

  const enRuta = vista?.ruta?.estado === 'en_curso';
  const p = vista?.configuracion.posiciones;
  useEffect(() => {
    if (!enRuta || !movilId || !p) return;
    gps.iniciarSeguimiento({
      movilId,
      intervaloMovimientoS: p.intervalo_movimiento_s,
      intervaloQuietoS: p.intervalo_quieto_s,
      precisionMaximaM: p.precision_maxima_m,
      envioLoteS: p.envio_lote_s,
    });
    return () => gps.detenerSeguimiento();
  }, [enRuta, movilId, p?.intervalo_movimiento_s, p?.intervalo_quieto_s, p?.precision_maxima_m, p?.envio_lote_s]);

  return null;
}

function Raiz() {
  return (
    <div className="h-full bg-stone-50 text-stone-900 dark:bg-stone-950 dark:text-stone-100">
      <Servicios />
      <Outlet />
    </div>
  );
}

const enrutador = createBrowserRouter([
  {
    element: <Raiz />,
    children: [
      {
        element: <Guardia />,
        children: [
          { path: '/acceso', element: <Acceso /> },
          { path: '/acceso/olvide', element: <Olvide /> },
          { path: '/acceso/celular', element: <AccesoCelular /> },
          { path: '/clave', element: <Clave /> },
          { path: '/movil', element: <Movil /> },
          { path: '/carga', element: <Carga /> },
          { path: '/ruta/armar', element: <ArmarRuta /> },
          { path: '/ruta', element: <RutaDelDia /> },
          { path: '/parada/:id', element: <Parada /> },
          { path: '/cierre', element: <Cierre /> },
          { path: '/perfil', element: <Perfil /> },
          { path: '/chat', element: conChat(<ListaChat />, <PantallaListaChat />) },
          { path: '/chat/:id', element: conChat(<ConversacionChat />, <PantallaConversacionChat />) },
          {
            path: '/mapa',
            element: (
              <Suspense fallback={<EsqueletoMapa pantalla />}>
                <Mapa />
              </Suspense>
            ),
          },
          {
            path: '/uso',
            element: (
              <Suspense
                fallback={
                  <MarcoGuia>
                    <EsqueletoGuia />
                  </MarcoGuia>
                }
              >
                <Uso />
              </Suspense>
            ),
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={enrutador} />;
}
